import React, { useState, useEffect, useContext, useRef } from 'react';
import './ImpersonateUserModal.css';
import Modal from '../Modal/Modal';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import ImpersonateUserRow from './ImpersonateUserRow';
import ActionButtons from '../ActionButtons/ActionButtons';
import CheckboxSelectionList from '../CheckboxSelectionList/CheckboxSelectionList';
import SelectionList from '../SelectionList/SelectionList';
import baseApi from '../../common/api/base';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import logComponent from '../../common/logComponent';

const USERS_PER_LOAD = 100;

const COLUMNS = [{
  heading: 'Name', style: { width: '48.3%' }
}, {
  heading: 'E-mail Address', style: { width: '42.8%' }
}, {
  heading: '', style: { width: '8.9%' }
}]

export default function ImpersonateUserModal({ showFocus: propsShowFocus, hide }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const { user, selectedImpersonationUser, hasToken, setSelectedImpersonationUserEmail } = environmentContext;


  const [selectedUser, setSelectedUser] = useState(selectedImpersonationUser);
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [groupTypeFilter, setGroupTypeFilter] = useState(user.groupTypeString === 'Status Solutions' ? ['Facility', 'Corporate'] : ['Facility']);
  const [users, setUsers] = useState(null);
  const [usersOnDeck, setUsersOnDeck] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const [trueSearchTerms, setTrueSearchTerms] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [showFocus, setShowFocus] = useState(false);
  const [searchTermUpdateTimeout, setSearchTermUpdateTimeout] = useState(null);
  const [isValid, setIsValid] = useState(null);

  const inputSearchTermsRef = useRef('');
  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const lastTabRef = useRef(null);

  const selectionList = useRef(null);
  const actionButtons = useRef(null);
  const mostRecentRequest = useRef('')

  if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal rendered'); }


  const setMostRecentRequest = (el) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal setMostRecentRequest'); }
    mostRecentRequest.current = el;
  }

  const setInputSearchTermsRef = (el) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal setInputSearchTermsRef'); }
    inputSearchTermsRef.current = el;
  }

  const setModalRef = (el) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal setModalRef'); }
    modalRef.current = el;
  }

  const setFirstTabRef = (el) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal setFirstTabRef'); }
    firstTabRef.current = el;
  }

  const setLastTabRef = (el) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal setLastTabRef'); }
    lastTabRef.current = el;
  }

  useEffect(() => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal onMount'); }
    firstTabRef.current.focus();
  }, [])

  useEffect(() => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal useEffect [propsShowFocus]'); }
    if (propsShowFocus)
      setShowFocus(propsShowFocus);

  }, [propsShowFocus])


  useEffect(() => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal useEffect [trueSearchTerms]'); }
    if (trueSearchTerms)
    loadUsers();

  }, [trueSearchTerms])


  const updateSearchTerms = (e) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal updateSearchTerms'); }
    if (searchTermUpdateTimeout) clearTimeout(searchTermUpdateTimeout);

    setInputSearchTermsRef(e.target.value);
    setSearchTermUpdateTimeout(setTimeout(updateTrueSearchTerms, 350));
  }

  const updateTrueSearchTerms = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal updateTrueSearchTerms'); }
    setTrueSearchTerms(inputSearchTermsRef.current);
    setSearchTermUpdateTimeout(null);
  }

  const loadUsers = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal loadUsers'); }
    if (!hasToken) return;

    if (!trueSearchTerms || trueSearchTerms.length < 3) {
      if (!trueSearchTerms) {
        setUsers(null);
        setUsersOnDeck(null);
      }
      return;
    }

    let timestamp = Date.now();
    setUsers(null);
    setUsersOnDeck(null);
    setUsersLoading(true);
    setMostRecentRequest(timestamp);

    const queryObject = {
      SearchValues: trueSearchTerms,
      SortDesc: false,
      NumberToSkip: 0,
      NumberToTake: USERS_PER_LOAD,
      FilterBy: {
        GroupTypeEnum: groupTypeFilter,
        Status: ['Active']
      }
    }

    baseApi.User.GetUsersForCurrentUser(queryObject)
      .then(
        result => {
          if (timestamp !== mostRecentRequest.current) return;
          setUsers(result.data.filter((user, index) => index < USERS_PER_LOAD));
          setUsersOnDeck(result.data.filter((user, index) => index >= USERS_PER_LOAD));
          setUsersLoading(false);
          setUsersError(false);
          setMostRecentRequest(null);
        },
        error => {
          if (timestamp !== mostRecentRequest.current) return;

          setUsers(null);
          setUsersOnDeck(null);
          setUsersLoading(false);
          setUsersError(true);
          setMostRecentRequest(null);
        }
      )
  }

  const updateSelectedUser = (user) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal updateSelectedUser'); }
    setSelectedUser(user);
  }

  const selectUser = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal selectUser'); }
    // was load data
    if (selectedUser) {
      environmentContext.setSelectedImpersonationUserEmail(selectedUser.emailAddress);
      adminContext.setShouldUpdateGroups(true);
      handleValidSubmission();
    } else {
      handleInvalidSubmission();
    }
  }

  const cancelSelection = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal cancelSelection'); }
    startModalExit();
  }

  const startModalExit = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal startModalExit'); }
    setModalShouldExit(true);
  }

  const focusSelectionListFirstItem = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal focusSelectionListFirstItem'); }
    selectionList.current.focus();
  }

  const focusActionButtonsLastItem = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal focusActionButtonsLastItem'); }
    actionButtons.current.focus();
  }

  const handleKeyDown = (e) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal handleKeyDown'); }
    if (e.key === 'Escape') hide(true);
  }

  const handleTab = (e) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal handleTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || shiftKey) return;

    e.preventDefault();
    firstTabRef.current.focus();
  }

  const handleShiftTab = (e) => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    lastTabRef.current.focus();
  }

  const handleValidSubmission = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal handleValidSubmission'); }
    hideValidationErrors();
    setIsValid(true);

    startModalExit();

  }

  const handleInvalidSubmission = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal handleInvalidSubmission'); }
    showValidationErrors();
    modalRef.current.scrollTop = 0;
    firstTabRef.current.focus();

    setIsValid(false);
  }

  const showValidationErrors = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal showValidationErrors'); }
    setShowErrors(true);
  }

  const hideValidationErrors = () => {
    if (logComponent('ImpersonateUserModal')) { console.debug('ImpersonateUserModal hideValidationErrors'); }
    setShowErrors(false);
  }

  return (
    <Modal isVisible={true} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true}>
      <div
        className="Impersonate-User-Modal"
        onKeyDown={handleKeyDown}>
        <h3 className="Impersonate-User-Modal__heading">Mimic User</h3>
        <span
          tabIndex="0"
          className="Impersonate-User-Modal__focus-control"
          onFocus={focusActionButtonsLastItem} />

        <p className={'Impersonate-User-Modal__page-error' + (showErrors ? ' visible' : '')}>Please select a user to mimic.</p>

        <div className="Impersonate-User-Modal__search-input-label">User Search:</div>
        <div className="Impersonate-User-Modal__search-input-wrapper">
          <FontAwesomeIcon icon="search" className="Impersonate-User-Modal__search-icon" />
          <input
            type="text"
            className="Impersonate-User-Modal__search-input"
            placeholder="E-mail Address"
            value={inputSearchTermsRef.current}
            onChange={updateSearchTerms}
            ref={firstTabRef}
            onKeyDown={handleShiftTab} />
        </div>
        <CheckboxSelectionList
          ref={selectionList}
          columns={COLUMNS}
          items={users}
          itemComponent={ImpersonateUserRow}
          singularItemTerm="user"
          pluralItemTerm="users"
          searchTextFields={['emailAddress']}
          itemsLoading={usersLoading}
          itemsError={usersError}
          itemKeyField="userId"
          onSelect={updateSelectedUser} />
        <ActionButtons
          ref={actionButtons}
          mainAction={selectUser}
          mainActionText="Mimic user"
          mainActionPreIcon="eye"
          cancel={cancelSelection}
          cancelText="Cancel" />
      </div>
      <span
        tabIndex="0"
        className="Impersonate-User-Modal__focus-control"
        onFocus={focusSelectionListFirstItem}></span>
    </Modal>
  );
}